import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { API } from "../../backend";
import AuthService from "../../services/auth.service";
import { C, S } from "./styles";
import { SubscriberCheckout, PlanCards } from "./PlansPage";

// A fully public entry point — no login required. Meant to be linked
// directly from the marketing website. Visitor picks a plan (via the same
// PlanCards grid the in-app Plans tab uses) and goes through the *exact
// same* "Subscriber's Details + Your Plan" checkout already used inside
// the app (SubscriberCheckout, reused as-is here) — only the
// account-creation step underneath is swapped out, since the in-app flow
// finalizes via the authenticated company/create-client/ endpoint (adding
// a client company to an existing account), which an anonymous visitor
// can't call. This page instead finalizes via the public sign-up/
// endpoint, then logs the new account straight in.

const REQUIRED_FIELD_LABELS = {
  firstName: "First name is required",
  lastName: "Last name is required",
  phone: "Contact number is required",
  companyName: "Company name is required",
};
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PS = {
  shell: {
    minHeight: "100vh",
    background: C.bg,
    padding: "40px 16px",
    fontFamily: S.page.fontFamily,
    color: C.text,
  },
  container: { maxWidth: 980, margin: "0 auto" },
  header: { textAlign: "center", marginBottom: 32 },
  logo: { fontSize: 22, fontWeight: 800, color: C.primary, marginBottom: 6 },
  headline: { fontSize: 26, fontWeight: 800, margin: "0 0 8px" },
  subhead: { color: C.muted, fontSize: 14.5 },
  loginNudge: { textAlign: "center", marginTop: 24, fontSize: 13.5, color: C.muted },
  loginLink: {
    color: C.primary,
    fontWeight: 700,
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
    fontSize: "inherit",
  },
};

const generatePassword = () => {
  const upper = String.fromCharCode(65 + Math.floor(Math.random() * 26));
  const lower = Array.from({ length: 4 }, () =>
    String.fromCharCode(97 + Math.floor(Math.random() * 26))
  ).join("");
  const digits = Math.floor(1000 + Math.random() * 9000);
  return `${upper}${lower}@${digits}`;
};

function PublicSignup() {
  const navigate = useNavigate();

  const [step, setStep] = useState("plan"); // 'plan' | 'free' | 'checkout'
  const [freePlanName, setFreePlanName] = useState(null);

  const notify = (fn, msg) =>
    fn(msg, { position: "top-right", autoClose: 2500, theme: "light" });

  // Discover the real free plan's backend name once — the plan picker below
  // is cosmetic (Free / Professional), but the account has to be created
  // against a real SUB_PLANS row, so we look up whichever plan is actually
  // priced at $0 rather than guessing a name.
  useEffect(() => {
    axios
      .get(API + "paymentsub/subscription/plan/")
      .then((res) => {
        if (res.data && res.data.success === 1) {
          const list = res.data.data || [];
          const free = list.find(
            (p) => p.is_free_plan || Number(p.per_user_amount) === 0
          );
          if (free) setFreePlanName(free.plan_name);
        }
      })
      .catch(() => {});
  }, []);

  // Creates the account via the public sign-up/ endpoint, then logs it
  // straight in (same localStorage session shape the real Login page
  // sets up), so the visitor lands in the app already authenticated.
  const signUpAndLogIn = async ({
    firstName,
    lastName,
    email,
    phone,
    companyName,
    planName,
    userallow,
  }) => {
    if (!planName) {
      return {
        success: 0,
        error: "Couldn't determine the plan to sign you up for. Please try again.",
      };
    }
    const password = generatePassword();
    try {
      const res = await axios.post(API + "sign-up/", {
        name: `${firstName} ${lastName}`.trim(),
        company_name: companyName,
        // Not collected on this form — the backend requires a value, this
        // one just isn't shown to the visitor.
        address: "Not provided",
        city: "Not provided",
        phone_number: phone,
        plan: planName,
        userallow,
        user: { email, username: email, password },
      });
      if (!res.data || res.data.success !== 1) {
        const err = res.data && res.data.error;
        return {
          success: 0,
          error: typeof err === "string" ? err : "Couldn't create your account. Please try again.",
        };
      }

      const loginRes = await AuthService.login(email, password);
      if (!loginRes || !loginRes.access) {
        // Account exists either way — just can't auto-login this instant.
        return {
          success: 1,
          message: "Your account was created. Please log in to continue.",
          client_company_name: companyName,
          client_email: email,
          needsManualLogin: true,
        };
      }

      return {
        success: 1,
        message: "Your account has been created and you're now logged in.",
        client_company_name: companyName,
        client_email: email,
      };
    } catch (error) {
      return { success: 0, error: "Couldn't create your account. Please try again." };
    }
  };

  const handleCheckoutSuccess = (result) => {
    if (result && result.needsManualLogin) {
      notify(toast.success, result.message);
      setTimeout(() => navigate("/login"), 1200);
      return;
    }
    window.location.href = "/";
  };

  return (
    <div style={PS.shell}>
      <ToastContainer />
      <div style={PS.container}>
        <div style={PS.header}>
          <div style={PS.logo}>ATHENA</div>
          <h1 style={PS.headline}>Get started with Athena Fintech</h1>
          <p style={PS.subhead}>Choose a plan and create your account — no login needed.</p>
        </div>

        {step === "plan" && (
          <PlanCards
            onFreeClick={() => setStep("free")}
            onProClick={() => setStep("checkout")}
          />
        )}

        {step === "checkout" && (
          <SubscriberCheckout
            initialDetails={{}}
            backLabel="Back to Plans"
            onExit={() => setStep("plan")}
            onSuccess={handleCheckoutSuccess}
            publicMode
            createAccount={({ subDetails, checkoutUsers }) =>
              signUpAndLogIn({
                firstName: subDetails.firstName.trim(),
                lastName: subDetails.lastName.trim(),
                email: subDetails.email.trim(),
                phone: subDetails.phone.trim(),
                companyName: subDetails.companyName.trim(),
                planName: "Annual",
                userallow: checkoutUsers,
              })
            }
          />
        )}

        {step === "free" && (
          <FreeSignupForm
            onBack={() => setStep("plan")}
            onSubmit={async (values) => {
              const result = await signUpAndLogIn({
                ...values,
                planName: freePlanName,
                userallow: 1,
              });
              if (result.success === 1) {
                handleCheckoutSuccess(result);
              } else {
                notify(toast.error, result.error || "Couldn't create your account. Please try again.");
              }
              return result;
            }}
          />
        )}

        {step !== "checkout" && (
          <p style={PS.loginNudge}>
            Already have an account?{" "}
            <button type="button" style={PS.loginLink} onClick={() => navigate("/login")}>
              Log in
            </button>
          </p>
        )}
      </div>
    </div>
  );
}

// Same two-panel "Subscriber's Details + Your Plan" layout as the paid
// checkout (SubscriberCheckout in this same file), just with the amounts
// fixed at $0 and no payment step — the account is created the moment the
// visitor submits.
function FreeSignupForm({ onBack, onSubmit }) {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    companyName: "",
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState(false);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validate = () => {
    const errors = {};
    if (!form.firstName.trim()) errors.firstName = REQUIRED_FIELD_LABELS.firstName;
    if (!form.lastName.trim()) errors.lastName = REQUIRED_FIELD_LABELS.lastName;
    if (!form.email.trim()) {
      errors.email = "Email is required";
    } else if (!EMAIL_RE.test(form.email.trim())) {
      errors.email = "Enter a valid email address";
    }
    if (!form.phone.trim()) errors.phone = REQUIRED_FIELD_LABELS.phone;
    if (!form.companyName.trim()) errors.companyName = REQUIRED_FIELD_LABELS.companyName;
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }
    if (!termsAccepted) {
      setTermsError(true);
      return;
    }
    setTermsError(false);

    setSubmitting(true);
    await onSubmit({
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      companyName: form.companyName.trim(),
    });
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <button type="button" style={S.backLink} onClick={onBack}>
        <i className="fa-solid fa-arrow-left" />
        Back to Plans
      </button>

      <div style={S.checkoutGrid}>
        <div style={S.panel}>
          <h3 style={S.planTitle}>
            <span style={{ ...S.dot, background: C.success }} />
            Subscriber's Details
          </h3>
          <p style={S.planDesc}>
            We make payments easy. No need to negotiate with a sales team.
          </p>

          <div style={S.formGroup}>
            <label style={S.formLabel}>First Name *</label>
            <input
              style={{ ...S.formInput, ...(fieldErrors.firstName ? S.formInputError : {}) }}
              value={form.firstName}
              onChange={handleChange("firstName")}
            />
            {fieldErrors.firstName && <p style={S.fieldErrorText}>{fieldErrors.firstName}</p>}
          </div>
          <div style={S.formGroup}>
            <label style={S.formLabel}>Last Name *</label>
            <input
              style={{ ...S.formInput, ...(fieldErrors.lastName ? S.formInputError : {}) }}
              value={form.lastName}
              onChange={handleChange("lastName")}
            />
            {fieldErrors.lastName && <p style={S.fieldErrorText}>{fieldErrors.lastName}</p>}
          </div>
          <div style={S.formGroup}>
            <label style={S.formLabel}>Email ID *</label>
            <input
              type="email"
              style={{ ...S.formInput, ...(fieldErrors.email ? S.formInputError : {}) }}
              value={form.email}
              onChange={handleChange("email")}
            />
            {fieldErrors.email && <p style={S.fieldErrorText}>{fieldErrors.email}</p>}
          </div>
          <div style={S.formGroup}>
            <label style={S.formLabel}>Contact Number *</label>
            <input
              style={{ ...S.formInput, ...(fieldErrors.phone ? S.formInputError : {}) }}
              value={form.phone}
              onChange={handleChange("phone")}
            />
            {fieldErrors.phone && <p style={S.fieldErrorText}>{fieldErrors.phone}</p>}
          </div>
          <div style={{ ...S.formGroup, marginBottom: 0 }}>
            <label style={S.formLabel}>Company Name *</label>
            <input
              style={{ ...S.formInput, ...(fieldErrors.companyName ? S.formInputError : {}) }}
              value={form.companyName}
              onChange={handleChange("companyName")}
            />
            {fieldErrors.companyName && (
              <p style={S.fieldErrorText}>{fieldErrors.companyName}</p>
            )}
          </div>
        </div>

        <div style={S.panel}>
          <h3 style={S.planTitle}>
            <span style={{ ...S.dot, background: "#ef4444" }} />
            Your Plan
          </h3>
          <p style={S.planDesc}>Please check your subscription cost carefully.</p>

          <div style={S.planRow}>
            <span>Plan</span>
            <strong>Free</strong>
          </div>
          <div style={S.planRow}>
            <span>Subscription Cost per User</span>
            <strong>$0/Month</strong>
          </div>
          <div style={S.planRow}>
            <span>Number of Users</span>
            <strong>1 User</strong>
          </div>
          <div style={S.planRow}>
            <span>Add-ons</span>
            <span>$0.00</span>
          </div>
          <div style={S.planRow}>
            <span>Discount</span>
            <span>$0.00</span>
          </div>
          <div style={{ ...S.planRow, borderBottom: "none" }}>
            <strong>Total Payable</strong>
            <strong style={{ fontSize: 18 }}>$0.00</strong>
          </div>
        </div>
      </div>

      <div style={S.termsBox}>
        <label style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13 }}>
          <input
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => {
              setTermsAccepted(e.target.checked);
              if (e.target.checked) setTermsError(false);
            }}
            style={{ marginTop: 3 }}
          />
          <span>
            I agree to all the Terms and Conditions defined here.
            <div style={{ color: C.muted, fontSize: 12, marginTop: 4 }}>
              The Free plan is $0 — nothing is ever charged.
            </div>
          </span>
        </label>
        {termsError && <p style={S.fieldErrorText}>Please accept the terms and conditions</p>}
        <button
          type="submit"
          disabled={submitting}
          style={{ ...S.btn, ...S.btnAuto, ...S.btnPrimary, marginTop: 14 }}
        >
          {submitting ? "Creating Account..." : "Create Free Account"}
        </button>
      </div>
    </form>
  );
}

export default PublicSignup;
